class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 150));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    url.searchParams.set('q', search_term);
    // Without an explicit list Shopify falls back to the store's Search &
    // Discovery defaults, which can leave the "Suggestions" column empty.
    url.searchParams.set('resources[type]', 'query,product,collection,article,page');
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    // Partial matches are applied to the end of the term, so matching on more
    // fields than the default gives usable results from the first characters.
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,vendor,tag'
    );
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    return url;
  }

  /**
   * The Predictive Search API is not available for every storefront locale -
   * Shopify answers 417 "Unsupported buyer locale", which is what the Hebrew
   * storefront gets. The regular search route applies the same "match the start
   * of the last term" prefix matching, so the section is re-rendered from there
   * instead and the snippet fills the same results container.
   */
  setupFallbackURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.search_url || '/search'}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${search_term}`;
    }
    url.searchParams.set('q', this.withShortTermWildcard(search_term));
    // Product-only is both what the dropdown shows and how the section knows
    // this is a suggestions lookup. See predictive-search.liquid.
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    return url;
  }

  /**
   * Storefront search only prefix-matches terms of three characters or more, so
   * a two letter query such as "קר" finds nothing on its own. An explicit
   * trailing wildcard restores matching from the very first characters typed;
   * longer terms are left untouched so their results stay unchanged.
   */
  withShortTermWildcard(term) {
    const lastTerm = term.split(/\s+/).pop() || '';
    if (lastTerm.length === 0 || lastTerm.length >= 3 || /[*"~:()]$/.test(lastTerm)) return term;

    return `${term}*`;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    // Drop any in-flight request: its response would otherwise be able to
    // overwrite the results for the characters typed since.
    this.abortController?.abort();
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    const controller = new AbortController();
    this.abortController = controller;

    this.fetchResults(url, controller.signal)
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // A newer request is already running, keep the loading state on.
        if (controller.signal.aborted) return;
        this.setLoadingState(false);
      });
  }

  fetchResults(url, signal) {
    if (this.predictiveSearchUnsupported) {
      return this.fetchText(this.setupFallbackURL(), signal);
    }

    return this.fetchText(url, signal)
      .then((responseText) => {
        // An unusable locale can also answer 200 with an empty results
        // container rather than an error, which would show a blank dropdown.
        if (this.getResultsHTML(responseText)) return responseText;

        this.predictiveSearchUnsupported = true;
        return this.fetchText(this.setupFallbackURL(), signal);
      })
      .catch((error) => {
        // Remember the failure so later keystrokes go straight to the fallback.
        if (error.name === 'AbortError' || this.predictiveSearchUnsupported) throw error;

        this.predictiveSearchUnsupported = true;
        return this.fetchText(this.setupFallbackURL(), signal);
      });
  }

  /** Returns the rendered results markup, or null when the section had none. */
  getResultsHTML(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const container = new DOMParser().parseFromString(responseText, 'text/html').getElementById(id);
    if (!container || container.textContent.trim().length === 0) return null;

    return container.innerHTML;
  }

  fetchText(url, signal) {
    return fetch(url, { signal }).then((response) => {
      if (!response.ok) throw new Error(`Search request failed with status ${response.status}`);
      return response.text();
    });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      console.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    const resultsHTML = this.getResultsHTML(responseText);
    if (resultsHTML === null) {
      console.error(`Element with id '${id}' not found in the parsed response.`);
      return;
    }

    this.searchContent?.classList.remove('hidden');
    targetElement.innerHTML = resultsHTML;

    if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
      document.body.classList.add('search-open');
    }
    this.classList.add('predictive-search-open');
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    // A response that lands after the search was closed or cleared would
    // otherwise repopulate the results.
    if (!isOpen) this.abortController?.abort();

    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
